import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";

const ACTIVITY_FIELD_KEYS = [
  "form.activity.democracy",
  "form.activity.humanrights",
  "form.activity.environment",
  "form.activity.youth",
  "form.activity.social",
  "form.activity.gender",
  "form.activity.culture",
  "form.activity.media",
  "form.activity.health",
  "form.activity.economic",
  "form.activity.research",
  "form.activity.other",
];

interface Project {
  id: number;
  value: string;
}

export default function RegistrationForm() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [projects, setProjects] = useState<Project[]>([
    { id: 1, value: "" },
    { id: 2, value: "" },
    { id: 3, value: "" },
  ]);

  const addProject = () => {
    if (projects.length < 5) {
      setProjects((prev) => [...prev, { id: prev.length + 1, value: "" }]);
    }
  };

  const updateProject = (id: number, value: string) => {
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, value } : p)));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    projects.forEach((p, i) => {
      formData.set(`project_${i + 1}`, p.value);
    });

    const data = new URLSearchParams(formData as unknown as URLSearchParams);
    setStatus("sending");

    try {
      const res = await fetch("https://readdy.ai/api/form/d73bi0qfp7tlt849nhk0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setProjects([
          { id: 1, value: "" },
          { id: 2, value: "" },
          { id: 3, value: "" },
        ]);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const inputClass =
    "w-full px-4 py-3 text-sm text-navy bg-white border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors";
  const labelClass =
    "block text-xs font-medium text-navy/60 uppercase tracking-wider mb-2";

  if (status === "success") {
    return (
      <div className="flex flex-col items-start justify-center py-16">
        <div className="w-14 h-14 flex items-center justify-center mb-5">
          <i className="ri-checkbox-circle-line text-gold text-6xl" />
        </div>
        <h3 className="text-2xl font-bold text-navy mb-3">{t("form.success.title")}</h3>
        <p className="text-navy/60 text-base leading-relaxed max-w-md">
          {t("form.success.p")}
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-8 text-sm text-gold font-semibold hover:underline cursor-pointer"
        >
          {t("form.success.again")}
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="ngo-registration-form"
      onSubmit={handleSubmit}
      className="space-y-8"
    >
      {/* Section 1 — Organisation */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-gold font-semibold mb-6 pb-3 border-b border-cool-gray-200">
          {t("form.section.org")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className={labelClass} htmlFor="reg-name">
              {t("form.org.name.label")}
            </label>
            <input
              id="reg-name"
              name="organization_name"
              type="text"
              required
              placeholder={t("form.org.name.placeholder")}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="reg-field">
              {t("form.org.field.label")}
            </label>
            <select
              id="reg-field"
              name="activity_field"
              required
              className={`${inputClass} cursor-pointer`}
              defaultValue=""
            >
              <option value="" disabled>{t("form.org.field.placeholder")}</option>
              {ACTIVITY_FIELD_KEYS.map((key) => (
                <option key={key} value={t(key)}>{t(key)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="reg-address">
              {t("form.org.address.label")}
            </label>
            <input
              id="reg-address"
              name="address"
              type="text"
              required
              placeholder={t("form.org.address.placeholder")}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="reg-phone">
              {t("form.org.phone.label")}
            </label>
            <input
              id="reg-phone"
              name="phone"
              type="tel"
              required
              placeholder={t("form.org.phone.placeholder")}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="reg-email">
              {t("form.org.email.label")}
            </label>
            <input
              id="reg-email"
              name="email"
              type="email"
              required
              placeholder={t("form.org.email.placeholder")}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Section 2 — Projects */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-gold font-semibold mb-1 pb-3 border-b border-cool-gray-200">
          {t("form.section.projects")}
        </h3>
        <p className="text-xs text-navy/50 mt-3 mb-6">
          {t("form.projects.note")}
        </p>
        <div className="space-y-4">
          {projects.map((project, i) => (
            <div key={project.id}>
              <label className={labelClass}>
                {t("form.projects.label", { n: i + 1 })}{" "}
                {i < 3 ? "*" : (
                  <span className="normal-case tracking-normal text-navy/35">
                    {t("form.projects.optional")}
                  </span>
                )}
              </label>
              <textarea
                required={i < 3}
                maxLength={300}
                rows={2}
                value={project.value}
                onChange={(e) => updateProject(project.id, e.target.value)}
                placeholder={t("form.projects.placeholder")}
                className={`${inputClass} resize-none`}
              />
              <p className="text-right text-xs text-navy/30 mt-1">{project.value.length}/300</p>
            </div>
          ))}
        </div>
        {projects.length < 5 && (
          <button
            type="button"
            onClick={addProject}
            className="mt-3 inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy font-medium transition-colors cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-circle-line text-base" />
            </span>
            {t("form.projects.add", { n: projects.length })}
          </button>
        )}
      </div>

      {/* Section 3 — Contact person */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-gold font-semibold mb-6 pb-3 border-b border-cool-gray-200">
          {t("form.section.contact")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClass} htmlFor="reg-contact-name">
              {t("form.contact.name.label")}
            </label>
            <input
              id="reg-contact-name"
              name="contact_name"
              type="text"
              required
              placeholder={t("form.contact.name.placeholder")}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="reg-contact-role">
              {t("form.contact.role.label")}
            </label>
            <input
              id="reg-contact-role"
              name="contact_function"
              type="text"
              required
              placeholder={t("form.contact.role.placeholder")}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {status === "error" && (
        <p className="text-red-500 text-sm">
          {t("form.error")}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="w-full py-4 bg-navy text-white text-sm font-semibold rounded-md hover:bg-navy-light transition-colors duration-200 disabled:opacity-60 cursor-pointer whitespace-nowrap"
      >
        {status === "sending" ? t("form.submitting") : t("form.submit")}
      </button>
    </form>
  );
}
